import { useEffect, useRef, useState } from 'react';
import styled from '@/styles';
import { DependabilityV2 } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import ScoreRing from './ScoreRing';
import TierPill from './TierPill';
import TierPopover from './TierPopover';
import StatusBadgeRow from './StatusBadgeRow';
import StatCards from './StatCards';
import ActivityFeed from './ActivityFeed';
import ScoreHistoryModal from './ScoreHistoryModal';
import ActivationBreakdownModal from './ActivationBreakdownModal';
import RecentActivityModal from './RecentActivityModal';
import useDependabilityEvents from './useDependabilityEvents';

// Deliberately NOT elements/Card: the 2.0-Baseline card style is flat and
// outlined, which would mean negating Card's shadow and radius tokens.
const Wrapper = styled('div', {
  width: '100%',
  backgroundColor: 'white',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  borderRadius: '12px',
  border: `1px solid ${dependabilityV2Colors.depBorder}`,
});
const TopSection = styled('div', {
  display: 'flex',
  gap: '32px',
  // ring column is vertically centered against the taller right column
  alignItems: 'center',
  flexWrap: 'wrap',
  paddingBottom: '24px',
  borderBottom: `1px solid ${dependabilityV2Colors.depBorder}`,
});
const ScoreColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
});
const RightColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flex: '1 1 320px',
  minWidth: 0,
});
// GRAVY-10347 (#2 "stackable pills"): badges left, tier pill pinned top-right.
// Previously this row was `space-between` + `flex-wrap`, so once the widened
// StatusBadgeRow (no-show / pools / late check-ins / late cancels) filled the
// row the tier pill wrapped to a NEW line — and a lone space-between item on its
// own line lands LEFT, dropping the pill below the badges on the left. Now the
// row does NOT wrap; the badges take a flex-grow group that wraps WITHIN itself
// (StatusBadgeRow's own Row is flex-wrap) while the pill keeps its right slot.
const BadgePillRow = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
});
// The badge group shrinks (min-width:0) so its badges stack under themselves in
// a flex row instead of pushing the pill out of the row.
const BadgeGroup = styled('div', {
  flex: '1 1 auto',
  minWidth: 0,
});
const Breakdown = styled('button', {
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'center',
  color: dependabilityV2Colors.depPositive,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '$sm',
  textDecoration: 'underline',
});

const FEED_PAGE_SIZE = 3;

interface Props {
  workerId: number;
  data: DependabilityV2;
}

const DependabilityV2Card = ({ workerId, data }: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  // GRAVY-10347: the tier popover measures this wrapper to place itself clamped
  // inside the card (never over the header or under the sidebar).
  const tierAnchorRef = useRef<HTMLSpanElement>(null);
  // The inline feed shows only the first page; "View more" opens the
  // activity modal (view-activities frame) rather than growing the card.
  const { events, loading, loaded, error, loadPage, hasMore } =
    useDependabilityEvents(workerId, FEED_PAGE_SIZE);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  // GRAVY-10429: activation workers get the dedicated Activation breakdown
  // modal; reliability workers keep ScoreHistoryModal. Guarded on the payload
  // block being present too, so a rolling-deploy backend that reports
  // scoring_mode='activation' without the new block still falls back to the
  // reliability breakdown (which renders the activation footer copy) rather
  // than opening an empty modal.
  const showActivationBreakdown =
    data.scoring_mode === 'activation' && !!data.activation_breakdown;

  return (
    <Wrapper data-testid="dependability-v2-card">
      <TopSection>
        <ScoreColumn data-testid="dependability-score-col">
          <ScoreRing score={data.score} tier={data.tier} />
          <Breakdown type="button" onClick={() => setHistoryOpen(true)}>
            See breakdown
          </Breakdown>
        </ScoreColumn>
        <RightColumn>
          <BadgePillRow>
            <BadgeGroup data-testid="dependability-badge-group">
              <StatusBadgeRow badges={data.badges} />
            </BadgeGroup>
            <span
              ref={tierAnchorRef}
              // flex:none + align-self:flex-start keeps the pill in its top-right
              // slot even as the badges wrap onto multiple lines beside it.
              style={{
                position: 'relative',
                display: 'inline-flex',
                flex: '0 0 auto',
                alignSelf: 'flex-start',
              }}
            >
              <TierPill
                tier={data.tier}
                label={data.tier_label}
                onInfoClick={() => setPopoverOpen(true)}
                expanded={popoverOpen}
                accountabilityStatus={data.accountability_status}
                isSuspended={data.is_suspended}
              />
              <TierPopover
                isOpen={popoverOpen}
                onRequestClose={() => setPopoverOpen(false)}
                anchorRef={tierAnchorRef}
                // fallback static side when no anchor is measured (unit tests)
                justify="right"
                boundaries={data.tier_boundaries}
                score={data.score}
                tier={data.tier}
                completedShifts={data.completed_shifts_count}
                scoringMode={data.scoring_mode}
              />
            </span>
          </BadgePillRow>
          <StatCards stats={data.stats} />
        </RightColumn>
      </TopSection>
      <ActivityFeed
        events={events}
        hasMore={hasMore}
        loading={loading || !loaded}
        error={error}
        onRetry={() => loadPage(1)}
        onViewMore={() => setActivityOpen(true)}
      />
      <RecentActivityModal
        workerId={workerId}
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
      />
      {showActivationBreakdown ? (
        <ActivationBreakdownModal
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          breakdown={data.activation_breakdown!}
        />
      ) : (
        <ScoreHistoryModal
          workerId={workerId}
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          signals={data.signals}
          completedShifts={data.completed_shifts_count}
          scoringMode={data.scoring_mode}
        />
      )}
    </Wrapper>
  );
};

export default DependabilityV2Card;
